import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { portal } from "@/lib/api";
import { AvatarView } from "@/components/AvatarView";
import { Sparkles, GraduationCap, User, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState(null);
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);

  const doLogin = async () => {
    setLoading(true);
    try { await login(form.username.trim(), form.password); toast.success("¡Bienvenido!"); navigate("/"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo iniciar sesión"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center overflow-hidden mb-3"><img src="https://customer-assets-4nw71qhi.emergentagent.net/job_dojo-gamified/artifacts/q9a4jl7m_Cyberon-LogoW-T.png" alt="Cyberon" className="w-12 h-12 object-contain" /></div>
          <h1 className="font-display text-3xl font-extrabold">Cyberon <span className="text-primary">Dojo</span></h1>
        </div>
        <div className="bg-card rounded-3xl border border-border p-6 shadow-lg">
          {!mode && (
            <div className="space-y-3">
              <p className="text-center text-sm text-muted-foreground mb-2">{t("accessAs")}</p>
              <button data-testid="access-teacher" onClick={() => setMode("teacher")} className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-border hover:border-primary hover:-translate-y-0.5 transition-transform">
                <div className="w-11 h-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><GraduationCap className="w-6 h-6" /></div>
                <div className="text-left"><div className="font-bold">{t("teacher")}</div><div className="text-xs text-muted-foreground">{t("username")} + {t("password")}</div></div>
              </button>
              <button data-testid="access-student" onClick={() => navigate("/portal/alumno")} className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-border hover:border-secondary hover:-translate-y-0.5 transition-transform">
                <div className="w-11 h-11 rounded-xl bg-secondary/15 text-secondary flex items-center justify-center"><User className="w-6 h-6" /></div>
                <div className="text-left"><div className="font-bold">{t("student")}</div><div className="text-xs text-muted-foreground">{t("classCode")}</div></div>
              </button>
            </div>
          )}
          {mode === "teacher" && (
            <div className="space-y-4">
              <button onClick={() => setMode(null)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> {t("accessAs")}</button>
              <div><label className="text-sm font-semibold">{t("username")}</label><Input data-testid="login-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="rounded-xl mt-1" onKeyDown={(e) => e.key === "Enter" && doLogin()} /></div>
              <div><label className="text-sm font-semibold">{t("password")}</label><Input data-testid="login-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1" onKeyDown={(e) => e.key === "Enter" && doLogin()} /></div>
              <Button data-testid="login-submit" onClick={doLogin} disabled={loading} className="w-full rounded-full h-11 font-bold">{loading ? "…" : t("login")}</Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
